import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiSave,
  FiEye,
  FiEdit,
  FiBold,
  FiItalic,
  FiList,
  FiLink,
  FiImage,
  FiChevronDown,
  FiArrowLeft,
  FiExternalLink,
} from "react-icons/fi";
import { FaTicketAlt, FaRegCalendarAlt } from "react-icons/fa";
import { MdAccessTimeFilled } from "react-icons/md";

import AdminContainer from "../../../components/utils/AdminContainer";

import { selectBandsDropdown } from "../../../store/slices/bands/slice";
import { getBandsForDropdown } from "../../../store/slices/bands/asyncThunks";
import {
  getNewsDetails,
  updateNewsDetails,
  createNewsDetails,
} from "../../../store/slices/news/asyncThunks";
import { selectNewsDetails } from "../../../store/slices/news/slice";
import { getNewsTypeLabel } from "../../../utils/utils";

import {
  PageHeader,
  PageTitle,
  BackLink,
  SaveButton,
  EditorContainer,
  EditorGrid,
  EditorColumn,
  PreviewColumn,
  FormCard,
  FormGrid,
  FormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  FormSelect,
  Relative,
  ToolbarContainer,
  Toolbar,
  ToolbarButton,
  ToolbarGroup,
  EditorTextarea,
  PreviewCard,
  PreviewTitle,
  PreviewContent,
  PreviewNewsCard,
  NewsContent,
  NewsExcerpt,
  NewsFooter,
  NewsActions,
  ActionBtn,
  CheckboxGroup,
  CheckboxLabel,
  TabContainer,
  Tab,
  TabContent,
  ArticleTags,
  Tag,
} from "./AdminNewsDetailsPage.styles";
import { toast } from "../../../components/modern-ui/sonner";

const AdminNewsDetailsPage = () => {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const editorRef = useRef(null);

  const isEditing = Boolean(newsId && newsId !== "new");

  const {
    data: newsData,
    loading,
    updateLoading,
  } = useSelector(selectNewsDetails);

  const { data: bandsData, loading: bandsLoading } =
    useSelector(selectBandsDropdown);

  const [activeTab, setActiveTab] = useState("editor");

  const [formData, setFormData] = useState({
    title: "",
    bandId: "",
    type: "",
    text: "",
    content: "",
    location: "",
    venue: "",
    time: "",
    date: "",
    externalLink: "",
    ticketLink: "",
    isFeatured: false,
    active: false,
  });

  const [newsImage, setNewsImage] = useState(null);
  const [newsImagePreview, setNewsImagePreview] = useState(null);

  useEffect(() => {
    dispatch(getBandsForDropdown());

    if (isEditing) {
      dispatch(getNewsDetails(newsId));
    }
  }, [dispatch, newsId, isEditing]);

  useEffect(() => {
    if (isEditing && newsData) {
      setFormData({
        title: newsData.title || "",
        bandId: newsData.bandId || "",
        type: newsData.type || "",
        text: newsData.text || "",
        content: newsData.content || newsData.text || "",
        location: newsData.location || "",
        venue: newsData.venue || "",
        time: newsData.time || "",
        date: newsData.date ? newsData.date.split("T")[0] : "",
        externalLink: newsData.externalLink || "",
        ticketLink: newsData.ticketLink || "",
        isFeatured: newsData.isFeatured || false,
        active: newsData.active || false,
      });

      if (newsData.mainImage) {
        setNewsImagePreview(newsData.mainImage);
      }
    }
  }, [newsData, isEditing]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Slika je prevelika. Maksimalna veličina je 5MB.");
        return;
      }

      if (!file.type.startsWith("image/")) {
        toast.error("Molimo izaberite sliku (JPG, PNG, itd.)");
        return;
      }

      setNewsImage(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        setNewsImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setNewsImage(null);
    setNewsImagePreview(null);
    const input = document.getElementById("news-image-input");
    if (input) input.value = "";
  };

  const handleSave = async () => {
    try {
      const formDataToSend = new FormData();

      formDataToSend.append("title", formData.title);
      formDataToSend.append("text", formData.text || "");
      formDataToSend.append("content", formData.content || "");
      formDataToSend.append("type", formData.type);
      formDataToSend.append("bandId", parseInt(formData.bandId));
      formDataToSend.append("date", formData.date || "");
      formDataToSend.append("time", formData.time || "");
      formDataToSend.append("location", formData.location || "");
      formDataToSend.append("venue", formData.venue || "");
      formDataToSend.append("externalLink", formData.externalLink || "");
      formDataToSend.append("ticketLink", formData.ticketLink || "");
      formDataToSend.append("isFeatured", formData.isFeatured);
      formDataToSend.append("active", formData.active);

      if (newsImage) {
        formDataToSend.append("newsImages", newsImage);
      }

      if (isEditing) {
        await dispatch(
          updateNewsDetails({ id: newsId, data: formDataToSend })
        ).unwrap();

        toast.success("Novost je uspešno ažurirana!");
      } else {
        const result = await dispatch(
          createNewsDetails(formDataToSend)
        ).unwrap();

        toast.success("Novost je uspešno kreirana!");

        navigate(`/admin-panel/novost/${result.data.id}`);
      }
    } catch (error) {
      toast.error(
        "Greška pri čuvanju: " + (error.message || "Neočekivana greška")
      );
    }
  };

  // Toolbar functions
  const insertAtCursor = (text) => {
    if (editorRef.current) {
      const start = editorRef.current.selectionStart;
      const end = editorRef.current.selectionEnd;
      const content = formData.content;
      const newContent =
        content.substring(0, start) + text + content.substring(end);
      handleInputChange("content", newContent);

      setTimeout(() => {
        editorRef.current.focus();
        editorRef.current.setSelectionRange(
          start + text.length,
          start + text.length
        );
      }, 10);
    }
  };

  const wrapSelection = (startTag, endTag) => {
    if (editorRef.current) {
      const start = editorRef.current.selectionStart;
      const end = editorRef.current.selectionEnd;
      const selectedText = formData.content.substring(start, end);
      const wrappedText = startTag + selectedText + endTag;

      const newContent =
        formData.content.substring(0, start) +
        wrappedText +
        formData.content.substring(end);
      handleInputChange("content", newContent);

      setTimeout(() => {
        editorRef.current.focus();
        editorRef.current.setSelectionRange(
          start + startTag.length,
          start + startTag.length + selectedText.length
        );
      }, 10);
    }
  };

  const toolbarActions = {
    bold: () => wrapSelection("<strong>", "</strong>"),
    italic: () => wrapSelection("<em>", "</em>"),
    h2: () => insertAtCursor("\n<h2>Naslov</h2>\n"),
    h3: () => insertAtCursor("\n<h3>Podnaslov</h3>\n"),
    paragraph: () => insertAtCursor("\n<p>Tekst paragrafa...</p>\n"),
    list: () =>
      insertAtCursor("\n<ul>\n<li>Stavka 1</li>\n<li>Stavka 2</li>\n</ul>\n"),
    link: () => wrapSelection('<a href="https://example.com">', "</a>"),
    image: () => insertAtCursor('<img src="image-url.jpg" alt="Opis slike" />'),
    quote: () => insertAtCursor("\n<blockquote>Citat tekst...</blockquote>\n"),
  };

  // Preview component
  const renderPreview = () => {
    const selectedBand = bandsData.find((band) => band.id == formData.bandId);
    const bandName = selectedBand ? selectedBand.name : "";

    return (
      <PreviewNewsCard>
        <NewsContent>
          <NewsExcerpt
            dangerouslySetInnerHTML={{
              __html: formData.content || "Unesite sadržaj novosti...",
            }}
          />

          {(formData.ticketLink || formData.externalLink) && (
            <NewsFooter>
              <NewsActions>
                {formData.externalLink && (
                  <ActionBtn $primary>
                    <FiExternalLink />
                    Opširnije
                  </ActionBtn>
                )}
                {formData.ticketLink && (
                  <ActionBtn>
                    <FaTicketAlt />
                    Karte
                  </ActionBtn>
                )}

                {selectedBand && (
                  <ArticleTags>
                    <Tag>{bandName}</Tag>
                    <Tag>{getNewsTypeLabel(formData.type)}</Tag>
                    {formData.location && <Tag>{formData.location}</Tag>}
                  </ArticleTags>
                )}
              </NewsActions>
            </NewsFooter>
          )}
        </NewsContent>
      </PreviewNewsCard>
    );
  };

  if (loading) {
    return (
      <AdminContainer>
        <div style={{ textAlign: "center", padding: "4rem" }}>
          <div className="spinner" />
          <p>Učitavam detalje novosti...</p>
        </div>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <PageHeader>
        <div>
          <BackLink
            onClick={() => {
              navigate("/admin-panel/novosti");
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 300);
            }}
          >
            <FiArrowLeft /> Nazad
          </BackLink>
          <PageTitle>
            {isEditing ? "Uređuj novost" : "Kreiraj novu novost"}
          </PageTitle>
        </div>

        <SaveButton
          onClick={handleSave}
          disabled={updateLoading}
          $hasChanges={true}
        >
          <FiSave />
          {updateLoading ? "Čuvam..." : "Sačuvaj novost"}
        </SaveButton>
      </PageHeader>

      {/* Basic Information Form */}
      <FormCard>
        <FormGrid>
          <FormGroup>
            <FormLabel>Naslov novosti</FormLabel>
            <FormInput
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              placeholder="Unesite naslov novosti"
              required
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Bend</FormLabel>
            <Relative>
              <FormSelect
                value={formData.bandId}
                onChange={(e) => handleInputChange("bandId", e.target.value)}
                required
                disabled={bandsLoading}
              >
                <option value="">
                  {bandsLoading ? "Učitavam bendove..." : "Izaberite bend..."}
                </option>
                {bandsData.map((band) => (
                  <option key={band.id} value={band.id}>
                    {band.name}
                  </option>
                ))}
              </FormSelect>
              <FiChevronDown
                color="#ff6b35"
                size={20}
                style={{
                  position: "absolute",
                  right: 15,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </Relative>
          </FormGroup>

          <FormGroup>
            <FormLabel>Tip novosti</FormLabel>
            <Relative>
              <FormSelect
                value={formData.type}
                onChange={(e) => handleInputChange("type", e.target.value)}
                required
              >
                <option value="">Izaberite tip...</option>
                <option value="concert">Koncert</option>
                <option value="album">Album</option>
                <option value="interview">Intervju</option>
                <option value="video">Video</option>
                <option value="award">Nagrada</option>
              </FormSelect>
              <FiChevronDown
                color="#ff6b35"
                size={20}
                style={{
                  position: "absolute",
                  right: 15,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </Relative>
          </FormGroup>

          <FormGroup>
            <FormLabel>Kratki opis</FormLabel>
            <FormTextarea
              rows={3}
              value={formData.text}
              onChange={(e) => handleInputChange("text", e.target.value)}
              placeholder="Kratki opis novosti za pregled"
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Grad (za koncerte)</FormLabel>
            <FormInput
              type="text"
              value={formData.location}
              onChange={(e) => handleInputChange("location", e.target.value)}
              placeholder="npr. Beograd"
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Mesto održavanja (za koncerte)</FormLabel>
            <FormInput
              type="text"
              value={formData.venue}
              onChange={(e) => handleInputChange("venue", e.target.value)}
              placeholder="npr. Stark Arena"
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Datum (za koncerte)</FormLabel>
            <Relative>
              <FormInput
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange("date", e.target.value)}
              />
              <FaRegCalendarAlt
                size={16}
                color={"#ff6b35"}
                style={{
                  position: "absolute",
                  right: ".8rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </Relative>
          </FormGroup>

          <FormGroup>
            <FormLabel>Vreme (za koncerte)</FormLabel>
            <Relative>
              <FormInput
                type="time"
                value={formData.time}
                onChange={(e) => handleInputChange("time", e.target.value)}
              />
              <MdAccessTimeFilled
                size={16}
                color={"#ff6b35"}
                style={{
                  position: "absolute",
                  right: ".8rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </Relative>
          </FormGroup>

          <FormGroup>
            <FormLabel>Link za karte</FormLabel>
            <FormInput
              type="url"
              value={formData.ticketLink}
              onChange={(e) => handleInputChange("ticketLink", e.target.value)}
              placeholder="https://tickets.rs/..."
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Eksterni link</FormLabel>
            <FormInput
              type="url"
              value={formData.externalLink}
              onChange={(e) =>
                handleInputChange("externalLink", e.target.value)
              }
              placeholder="https://..."
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Glavna slika novosti</FormLabel>
            <div>
              {newsImagePreview ? (
                <div style={{ position: "relative" }}>
                  <img
                    src={newsImagePreview}
                    alt="Pregled slike"
                    style={{
                      maxWidth: "300px",
                      maxHeight: "200px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      marginBottom: "1rem",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: "1rem",
                    }}
                  >
                    <label
                      htmlFor="news-image-input"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: "0.5rem 1rem",
                        background: "#ff6b35",
                        color: "white",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        border: "none",
                      }}
                    >
                      <FiImage /> Zameni sliku
                    </label>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: "0.5rem 1rem",
                        background: "#dc3545",
                        color: "white",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        border: "none",
                      }}
                    >
                      Ukloni sliku
                    </button>
                  </div>
                </div>
              ) : (
                <label
                  htmlFor="news-image-input"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.5rem 1rem",
                    background: "#ff6b35",
                    color: "white",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                  }}
                >
                  <FiImage color="#fff" /> Izaberite sliku
                </label>
              )}
              <input
                id="news-image-input"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: "none" }}
              />
            </div>
          </FormGroup>
          <div>
            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) =>
                    handleInputChange("active", e.target.checked)
                  }
                />
                Objavi odmah
              </CheckboxLabel>
            </CheckboxGroup>

            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={formData.isFeatured}
                  onChange={(e) =>
                    handleInputChange("isFeatured", e.target.checked)
                  }
                />
                Istakni novost (Featured)
              </CheckboxLabel>
            </CheckboxGroup>
          </div>
        </FormGrid>
      </FormCard>

      {/* Content Editor */}
      <EditorContainer>
        <TabContainer>
          <Tab
            $active={activeTab === "editor"}
            onClick={() => setActiveTab("editor")}
          >
            <FiEdit /> Editor
          </Tab>
          <Tab
            $active={activeTab === "preview"}
            onClick={() => setActiveTab("preview")}
          >
            <FiEye /> Pregled
          </Tab>
        </TabContainer>

        <TabContent>
          {activeTab === "editor" && (
            <EditorGrid>
              <EditorColumn>
                <ToolbarContainer>
                  <Toolbar>
                    <ToolbarGroup>
                      <ToolbarButton onClick={toolbarActions.bold} title="Bold">
                        <FiBold />
                      </ToolbarButton>
                      <ToolbarButton
                        onClick={toolbarActions.italic}
                        title="Italic"
                      >
                        <FiItalic />
                      </ToolbarButton>
                    </ToolbarGroup>

                    <ToolbarGroup>
                      <ToolbarButton
                        onClick={toolbarActions.h2}
                        title="Naslov H2"
                      >
                        H2
                      </ToolbarButton>
                      <ToolbarButton
                        onClick={toolbarActions.h3}
                        title="Naslov H3"
                      >
                        H3
                      </ToolbarButton>
                      <ToolbarButton
                        onClick={toolbarActions.paragraph}
                        title="Paragraf"
                      >
                        P
                      </ToolbarButton>
                    </ToolbarGroup>

                    <ToolbarGroup>
                      <ToolbarButton
                        onClick={toolbarActions.list}
                        title="Lista"
                      >
                        <FiList />
                      </ToolbarButton>
                      <ToolbarButton onClick={toolbarActions.link} title="Link">
                        <FiLink />
                      </ToolbarButton>
                      <ToolbarButton
                        onClick={toolbarActions.image}
                        title="Slika"
                      >
                        <FiImage />
                      </ToolbarButton>
                    </ToolbarGroup>

                    <ToolbarGroup>
                      <ToolbarButton
                        onClick={toolbarActions.quote}
                        title="Citat"
                      >
                        "
                      </ToolbarButton>
                    </ToolbarGroup>
                  </Toolbar>
                </ToolbarContainer>

                <EditorTextarea
                  ref={editorRef}
                  value={formData.content}
                  onChange={(e) => handleInputChange("content", e.target.value)}
                  placeholder="Unesite detaljni sadržaj novosti ovde... Možete koristiti HTML tagove."
                />
              </EditorColumn>

              <PreviewColumn>
                <PreviewCard>
                  <PreviewTitle>Live Preview</PreviewTitle>
                  <PreviewContent>{renderPreview()}</PreviewContent>
                </PreviewCard>
              </PreviewColumn>
            </EditorGrid>
          )}

          {activeTab === "preview" && (
            <div
              style={{
                padding: "2rem",

                borderRadius: "10px",
              }}
            >
              <PreviewTitle>Kako će novost izgledati:</PreviewTitle>
              <div style={{ maxWidth: "788px", margin: "2rem auto 0" }}>
                {renderPreview()}
              </div>
            </div>
          )}
        </TabContent>
      </EditorContainer>
    </AdminContainer>
  );
};

export default AdminNewsDetailsPage;
